import React from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RichTextEditorChecklist } from "@/components/ui/rich-text-editor-checklist";
import { Share2, Users } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DrawingContent {
  drawing: string;
  text: string;
}

interface DrawingCanvasProps {
  content: DrawingContent;
  onChange: (content: DrawingContent) => void;
  isSharing?: boolean;
  onSharingChange?: (sharing: boolean) => void;
  isCollaborative?: boolean;
  onCollaborativeChange?: (collaborative: boolean) => void;
  readonly?: boolean;
  showControls?: boolean;
  compact?: boolean;
  fullscreen?: boolean;
}

export function DrawingCanvas({
  content,
  onChange,
  isSharing = false,
  onSharingChange,
  isCollaborative = false,
  onCollaborativeChange,
  readonly = false,
  showControls = true,
  compact = false,
  fullscreen = false,
}: DrawingCanvasProps) {
  const handleTextChange = (text: string) => {
    onChange({ ...content, text });
  };

  const handleDrawingChange = (drawing: string) => {
    onChange({ ...content, drawing });
  };

  return (
    <div className={cn(
      "flex flex-col gap-4", 
      compact && "gap-2",
      fullscreen && "h-full"
    )}>
      {/* Sharing Controls (only show when callbacks are provided) */}
      {showControls && (onSharingChange || onCollaborativeChange) && (
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg shrink-0">
          {onSharingChange && (
            <Label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch 
                checked={isSharing} 
                onCheckedChange={onSharingChange}
              />
              <Share2 className="h-4 w-4" />
              Mit Kunde teilen
            </Label>
          )}
          {onCollaborativeChange && (
            <Label className={cn(
              "flex items-center gap-2 text-sm cursor-pointer",
              !isSharing && "opacity-50"
            )}>
              <Switch 
                checked={isCollaborative} 
                onCheckedChange={onCollaborativeChange}
                disabled={!isSharing}
              />
              <Users className="h-4 w-4" />
              Kollaboration
            </Label>
          )}
        </div>
      )}

      {/* Integrated Rich Text Editor with Canvas */}
      <RichTextEditorChecklist
        content={content.text}
        onChange={handleTextChange}
        enableCanvas={true}
        drawingData={content.drawing}
        onDrawingChange={handleDrawingChange}
        placeholder="Notizen zur Zeichnung..."
        minHeight={fullscreen ? "100%" : (compact ? "250px" : "400px")}
        showProgress={false}
        editable={!readonly}
        className={fullscreen ? "flex-1 min-h-0" : ""}
      />
    </div>
  );
}
